import { useContext, useMemo } from 'react';
import { GridContext } from 'contexts/GridContext';
import { ValidationMinMaxContext } from 'contexts/ValidationMinMaxContext';
import { Grid } from 'components/lib';
import { UMInputNumber } from '@urbanmetrix/um-kit';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { checkVolume } from 'services/gridLimitChecks';
import { getInputNumberRangeErrorMessage, isInputNumberInRange } from 'utils/inputNumberErrorMessage';
import { formatCubicMeters } from 'utils/formatCubicMeters';
import { GRID_NUMBER_LOCALE } from 'constants/grid';

type OtherInfoForSiteProps = {
  showHeightInput?: boolean;
  showVolumeSummary?: boolean;
  showSensorPointsSummary?: boolean;
  readOnly?: boolean;
  onBillingLinkClick?: () => void;
};

const OtherInfoForSite = ({
  showHeightInput = true,
  showVolumeSummary = true,
  showSensorPointsSummary = false,
  readOnly = false,
  onBillingLinkClick,
}: OtherInfoForSiteProps) => {
  const { gridParams, setGridParams, sensorPoints, volumeLimit, gridVolume } = useContext(GridContext);
  const { isValidGridMinMaxInputs, setIsValidGridMinMaxInputs } = useContext(ValidationMinMaxContext);
  const billingLinkPlaceholder = '[BILLING_LINK]';
  const volumeCheckResult = useMemo(
    () =>
      gridVolume != null
        ? checkVolume({
            totalVolume: gridVolume,
            volumeLimit,
            gridHeight: Number(gridParams.gridHeight),
            gridResolution: Number(gridParams.gridResolution),
            locale: GRID_NUMBER_LOCALE,
          })
        : null,
    [gridVolume, gridParams.gridHeight, gridParams.gridResolution, volumeLimit]
  );
  const volumeErrorMessageParts = String(volumeCheckResult?.message || '').split(billingLinkPlaceholder);
  const currentAnalysisVolumeLabel =
    gridVolume != null ? `${formatCubicMeters(gridVolume, GRID_NUMBER_LOCALE)} m³` : null;
  const volumeLabelFromMessage = useMemo(() => {
    const match = String(volumeCheckResult?.message || '').match(/([\d,'’]+)\s*m³/);
    return match ? `${match[1]} m³` : null;
  }, [volumeCheckResult?.message]);
  const analysisVolumeLabel = volumeLabelFromMessage ?? currentAnalysisVolumeLabel;
  const showVolumeError = showVolumeSummary && Boolean(volumeCheckResult);
  const showVolumeSuccess = showVolumeSummary && !volumeCheckResult && analysisVolumeLabel != null;
  const volumeSummaryToneClass = showVolumeError ? 'grid-summary-block__value--error' : '';
  const sensorPointsMessageParts = String(sensorPoints.message || '').split(billingLinkPlaceholder);
  const sensorPointsSummaryToneClass = sensorPoints.type === 'error' ? 'grid-summary-block__value--error' : '';
  const isGridHeightValid = isInputNumberInRange(gridParams.gridHeight, 1, 300);

  const handleInputsChange = (name: string | undefined, value: string | number, valid?: boolean) => {
    setGridParams({ ...gridParams, [name as string]: value });
    setIsValidGridMinMaxInputs({
      ...isValidGridMinMaxInputs,
      [name as string]: valid,
    });
  };

  return (
    <>
      {showHeightInput && (
        <div className="grid-height-section">
          <Grid cols="3">
            <UMInputNumber
              label={
                <span className="grid-inline-label-with-info">
                  <span>
                    Analysis Volume Height [m] <span style={{ color: '#d32f2f' }}>*</span>
                  </span>
                  <CustomTooltip
                    title="Typically the maximal building height. If you use a multiple of 3m, later in the 3D visualizer you will be able to switch to a coherent coarse 3x3x3m grid."
                    arrow
                  />
                </span>
              }
              value={gridParams.gridHeight}
              width={84}
              maxErrorMessageWidth={276}
              name="gridHeight"
              onChange={handleInputsChange}
              defaultValue={9}
              min={1}
              max={300}
              errorMessage={getInputNumberRangeErrorMessage(1, 300)}
              valid={isGridHeightValid}
              disabled={readOnly}
            />
          </Grid>
        </div>
      )}
      {(showVolumeSuccess || showVolumeError) && (
        <div className="grid-summary-block">
          <div className="grid-summary-block__label">Analysis Volume</div>
          {analysisVolumeLabel ? (
            <div className={`grid-summary-block__value ${volumeSummaryToneClass}`.trim()}>{analysisVolumeLabel}</div>
          ) : null}
          {showVolumeError && (
            <div className="grid-summary-block__message grid-summary-block__message--error">
              {volumeErrorMessageParts[0]}
              {volumeCheckResult?.showBillingLink ? (
                <>
                  <button type="button" className="grid-summary-block__billing-link" onClick={onBillingLinkClick}>
                    GO TO BILLING PAGE
                  </button>
                  {volumeErrorMessageParts.slice(1).join(billingLinkPlaceholder)}
                </>
              ) : (
                volumeErrorMessageParts.slice(1).join(billingLinkPlaceholder)
              )}
            </div>
          )}
        </div>
      )}
      {showSensorPointsSummary && (
        <div className="grid-summary-block">
          <div className="grid-summary-block__label">Sensor Points number</div>
          <div className={`grid-summary-block__value ${sensorPointsSummaryToneClass}`.trim()}>
            {sensorPoints.totalSensorPoints == null
              ? '\u00a0'
              : Math.round(sensorPoints.totalSensorPoints).toLocaleString(GRID_NUMBER_LOCALE)}
          </div>
          {sensorPoints.type === 'error' ? (
            <div className="grid-summary-block__message grid-summary-block__message--error">
              {sensorPointsMessageParts[0]}
              {sensorPoints.showBillingLink ? (
                <>
                  <button type="button" className="grid-summary-block__billing-link" onClick={onBillingLinkClick}>
                    GO TO BILLING PAGE
                  </button>
                  {sensorPointsMessageParts.slice(1).join(billingLinkPlaceholder)}
                </>
              ) : (
                sensorPointsMessageParts.slice(1).join(billingLinkPlaceholder)
              )}
            </div>
          ) : sensorPoints.type === 'warning' ? (
            <div className="grid-summary-block__message">{sensorPoints.message}</div>
          ) : null}
        </div>
      )}
    </>
  );
};

export default OtherInfoForSite;
